// Dr.Rx™ Application Entry Point
import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import ErrorBoundary from './components/ErrorBoundary';
import './index.css';

// --- Error Handling & Noise Suppression ---
// Filter out benign ResizeObserver notifications that do not affect clinical UX
const IGNORED_ERRORS = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications.'
];

const originalOnError = window.onerror;
window.onerror = (msg, url, line, col, error) => {
  if (IGNORED_ERRORS.some(ignoreMsg => String(msg).includes(ignoreMsg))) {
    return true;
  }
  if (originalOnError) return originalOnError.call(window, msg, url, line, col, error);
  return false;
};

const originalOnUnhandledRejection = window.onunhandledrejection;
window.onunhandledrejection = (event) => {
  if (IGNORED_ERRORS.some(ignoreMsg => String(event.reason).includes(ignoreMsg))) {
    event.preventDefault();
    return;
  }
  if (originalOnUnhandledRejection) originalOnUnhandledRejection.call(window, event);
};

// --- Service Worker Registration ---
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('./service-worker.js')
      .then(registration => {
        console.log('ServiceWorker registration successful with scope: ', registration.scope);
      })
      .catch(err => {
        console.log('ServiceWorker registration failed: ', err);
      });
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <HelmetProvider>
    <ToastProvider>
      <AuthProvider>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </AuthProvider>
    </ToastProvider>
  </HelmetProvider>
);
